import _ from 'underscore';
import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import hoistNonReactStatics from 'hoist-non-react-statics';

import { useAdsStudioTheme } from './ads-studio-theme-provider';

const emptyAdsStudioTheme = { theme: {}, getComponentStyles: () => { } };
const getDisplayName = Component => Component.displayName || Component.name || 'Component';

function useThemedStyles({ componentName, prefixClassName, getDefaultStyles }) {
  // get theme from ads studio themem context provider
  const { theme, getComponentStyles } = useAdsStudioTheme() || emptyAdsStudioTheme;

  // get themed styles
  const themedStyles = useMemo(() => {
    // check if there are any overriden styles for this component from theme provider
    let overridenStyles = {};
    if (getComponentStyles) {
      // it could be an function or an object
      const getOverridenStyles = getComponentStyles(componentName);
      overridenStyles = _.isFunction(getOverridenStyles) ? getOverridenStyles(theme || {}) : getOverridenStyles;
    }

    // default styles
    const defaultStyles = getDefaultStyles && getDefaultStyles(theme || {});

    // merge styles
    return { ...defaultStyles, ...overridenStyles };
  }, [componentName, getComponentStyles, getDefaultStyles, theme]);

  // create jss styles
  const useStyles = useMemo(
    () => createUseStyles(themedStyles, { name: prefixClassName || 'AS' }),
    [prefixClassName, themedStyles]
  );
  const classes = useStyles() || {};

  const styles = useMemo(() => ({ classes, themedStyles }), [classes, themedStyles]);
  return styles;
}

// use an external theme to decouple the wrapped components with a specific theme provider
// eslint-disable-next-line valid-jsdoc
/**
 * @deprecated this function is deprecated, and will be removed later
 */
export function withAdsStudioStyles(WrappedComponent, getDefaultStyles, options) {
  const {
    prefixClassName, // the prefix of the class names generated by react-jss
    useRawStyles, // also pass down the themed styles to the wrapped component, which may be used in Fluent UI components
    componentName, // the description name of WrappedComponent
  } = options || {};

  const finalComponentName = componentName || getDisplayName(WrappedComponent);

  const HOC = (props) => {
    const { classes, themedStyles } = useThemedStyles({ componentName: finalComponentName, prefixClassName, getDefaultStyles });
    return (
      <WrappedComponent {...props} classes={classes} themedStyles={useRawStyles ? themedStyles : undefined} />
    );
  };

  HOC.displayName = `withAdsStudioStyles(${finalComponentName})`;

  hoistNonReactStatics(HOC, WrappedComponent);

  return HOC;
}

